import React, { useState, useEffect, useCallback } from 'react';
import './GenerationHistory.css';

const STORAGE_KEY = 'fashionai-history';
const MAX_HISTORY = 10;

// ── Helpers ────────────────────────────────────────────────────
function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

function relativeTime(ts) {
  const diff = Date.now() - ts;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ── Public API: add entry to history ─────────────────────────
export function addToHistory({ prompt, imageUrl, variants = [], designId = null }) {
  if (!prompt || !imageUrl) return;

  const existing = loadHistory();
  // Avoid exact-prompt duplicates in a row
  if (existing[0]?.prompt === prompt && existing[0]?.imageUrl === imageUrl) return;

  const entry = {
    id: Date.now(),
    prompt,
    imageUrl,
    variants,
    designId,
    timestamp: Date.now(),
  };

  const updated = [entry, ...existing].slice(0, MAX_HISTORY);
  saveHistory(updated);

  // Notify the panel via a custom event
  window.dispatchEvent(new CustomEvent('fashionai:history-updated', { detail: updated }));
}

// ── Component ─────────────────────────────────────────────────
export default function GenerationHistory({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);

  // Load from localStorage and listen for updates
  useEffect(() => {
    setHistory(loadHistory());

    const onUpdate = (e) => setHistory(e.detail);
    window.addEventListener('fashionai:history-updated', onUpdate);
    return () => window.removeEventListener('fashionai:history-updated', onUpdate);
  }, []);

  const clearHistory = useCallback(() => {
    saveHistory([]);
    setHistory([]);
  }, []);

  const handleSelect = useCallback((entry) => {
    onSelect?.(entry);
    setOpen(false);
  }, [onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  return (
    <>
      {/* FAB trigger */}
      <button
        className="history-fab"
        onClick={() => setOpen(true)}
        title="Generation History"
        aria-label="Open generation history"
      >
        🕐
        {history.length > 0 && (
          <span className="history-fab-badge">{history.length}</span>
        )}
      </button>

      {/* Backdrop */}
      <div
        className={`history-backdrop${open ? ' open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`history-drawer${open ? ' open' : ''}`}
        aria-label="Generation History"
        role="complementary"
      >
        {/* Header */}
        <div className="history-header">
          <span className="history-title">
            <span className="history-title-icon">🕐</span>
            History
            {history.length > 0 && (
              <span className="history-count">{history.length}</span>
            )}
          </span>
          <button className="history-close" onClick={() => setOpen(false)} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Clear action */}
        {history.length > 0 && (
          <div className="history-actions">
            <button className="history-clear-btn" onClick={clearHistory}>
              🗑 Clear all
            </button>
          </div>
        )}

        {/* Content */}
        {history.length === 0 ? (
          <div className="history-empty">
            <div className="history-empty-icon">🎨</div>
            <p>No history yet</p>
            <span>Your generations will appear here</span>
          </div>
        ) : (
          <div className="history-list">
            {history.map((entry, idx) => (
              <div
                key={entry.id}
                className="history-item"
                onClick={() => handleSelect(entry)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleSelect(entry)}
                style={{ animationDelay: `${idx * 0.04}s` }}
              >
                {entry.imageUrl ? (
                  <img
                    src={entry.imageUrl}
                    alt={entry.prompt}
                    className="history-thumb"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                    }}
                  />
                ) : (
                  <div className="history-thumb-placeholder">🎨</div>
                )}
                <div className="history-item-meta">
                  <div className="history-item-prompt" title={entry.prompt}>
                    {entry.prompt}
                  </div>
                  <div className="history-item-time">{relativeTime(entry.timestamp)}</div>
                  {entry.variants?.length > 1 && (
                    <div className="history-item-variants">
                      {entry.variants.length} variants
                    </div>
                  )}
                </div>
                <button className="history-item-use" tabIndex={-1}>
                  Use →
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="history-footer">
          <p>Last {MAX_HISTORY} generations · Stored locally</p>
        </div>
      </aside>
    </>
  );
}
